const INITIAL_SOURCE_TYPES = new Set([
  'initial',
  'initial_analysis',
  'initial_filtering',
  'initial_screening',
  'screening',
]);

const normalizeId = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return String(value);
};

const addIds = (target, ...values) => {
  values.flat(Infinity).forEach((value) => {
    const normalized = normalizeId(value);
    if (normalized) target.add(normalized);
  });
};

const eventTimestamp = (event) => {
  const value = event?.evaluatedAt || event?.createdAt || event?.updatedAt || 0;
  if (typeof value === 'number') return value;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const eventRevision = (event) => {
  const value = Number(event?.revision ?? event?.scoreRevision?.revision ?? 0);
  return Number.isFinite(value) ? value : 0;
};

export const normalizeCandidateEvaluationEvents = (candidateEvaluation) => {
  const entries = Object.entries(candidateEvaluation?.events || {});
  return entries
    .map(([eventId, event]) => ({
      ...(event && typeof event === 'object' ? event : {}),
      eventId: normalizeId(event?.eventId || eventId),
    }))
    .filter((event) => event.eventId);
};

export const getEvaluationStatus = (event) => String(
  event?.status || event?.evaluationStatus || ''
).trim().toLowerCase().replace(/-/g, '_');

export const isEvaluationPending = (event) => new Set([
  'queued',
  'pending',
  'evaluating',
  'processing',
  'in_progress',
]).has(getEvaluationStatus(event));

export const isEvaluationManualReview = (event) => new Set([
  'manual_review',
  'needs_review',
  'human_review',
]).has(getEvaluationStatus(event));

export const isEvaluationFailure = (event) => new Set([
  'technical_failure',
  'evaluation_failed',
  'failed',
  'error',
]).has(getEvaluationStatus(event));

export const isInitialEvaluationEvent = (event) => INITIAL_SOURCE_TYPES.has(
  String(event?.sourceType || '').trim().toLowerCase().replace(/-/g, '_')
);

const collectStepIdentity = (step, relatedAssessment, relatedInterviews) => {
  const stageIds = new Set();
  const substageIds = new Set();
  const assessmentIds = new Set();
  const sessionIds = new Set();
  const explicitEventIds = new Set();

  addIds(stageIds, step?.stageId, step?.workflowStageId);
  addIds(
    substageIds,
    step?.id,
    step?.substageId,
    step?.workflowSubstageId,
    step?.workflowStepId,
  );
  addIds(
    assessmentIds,
    step?.assessmentId,
    step?.link,
    relatedAssessment?.assessmentId,
    relatedAssessment?.assessment?.assessmentId,
    relatedAssessment?.assessment?.id,
  );
  addIds(
    sessionIds,
    relatedAssessment?.sessionId,
    relatedAssessment?.liveReview?.sessionId,
    relatedAssessment?.response?.sessionId,
    (relatedInterviews || []).flatMap((interview) => [
      interview?.sessionId,
      interview?.aiInterviewSessionId,
      interview?.id,
    ]),
  );
  addIds(
    explicitEventIds,
    step?.evaluationEventId,
    step?.journeyEventId,
    relatedAssessment?.journeyEventId,
    relatedAssessment?.liveReview?.journeyEventId,
    (relatedInterviews || []).flatMap((interview) => [
      interview?.journeyEventId,
      interview?.candidateEvaluationEventId,
    ]),
  );

  return { stageIds, substageIds, assessmentIds, sessionIds, explicitEventIds };
};

const hasId = (set, value) => Boolean(normalizeId(value) && set.has(normalizeId(value)));

const eventBindingScore = (event, identity) => {
  if (identity.explicitEventIds.has(event.eventId)) return 1000;

  const eventStageId = normalizeId(event.stageId || event.workflowStageId);
  const eventSubstageId = normalizeId(
    event.substageId || event.workflowSubstageId || event.workflowStepId
  );
  const stageMatches = !eventStageId || identity.stageIds.has(eventStageId);
  if (!stageMatches) return 0;

  const sessionMatches = [event.sessionId, event.sourceId]
    .some((value) => hasId(identity.sessionIds, value));
  if (sessionMatches) return eventStageId ? 950 : 925;

  // When the backend persisted workflow identity, a different substage is a
  // hard mismatch even if both steps reuse the same assessment definition.
  if (eventSubstageId && !identity.substageIds.has(eventSubstageId)) return 0;

  if (eventSubstageId && identity.substageIds.has(eventSubstageId) && stageMatches) {
    return eventStageId ? 900 : 850;
  }

  const assessmentMatches = [event.assessmentId, event.sourceId, event.assignmentId]
    .some((value) => hasId(identity.assessmentIds, value));
  if (assessmentMatches && stageMatches) return eventStageId ? 800 : 750;

  return 0;
};

/**
 * Bind an evaluation to one configured workflow step using persisted identity.
 * We never fall back to the globally newest event: doing so can display an
 * interview result under a different department-specific assessment step.
 */
export const findEvaluationEventForStep = (
  candidateEvaluation,
  step,
  { relatedAssessment = null, relatedInterviews = [] } = {},
) => {
  const events = normalizeCandidateEvaluationEvents(candidateEvaluation);
  if (!step || events.length === 0) return null;

  if (step.id === 'initial-analysis' || step.type === 'initial-analysis') {
    const explicitInitialId = normalizeId(
      candidateEvaluation?.initialEventId || candidateEvaluation?.initial?.eventId
    );
    if (explicitInitialId) {
      return events.find((event) => event.eventId === explicitInitialId) || null;
    }

    // An old ledger may not have initialEventId. The baseline is the first
    // initial-screening event, not whichever evaluation happened most recently.
    return events
      .filter(isInitialEvaluationEvent)
      .sort((left, right) => eventTimestamp(left) - eventTimestamp(right))[0] || null;
  }

  const identity = collectStepIdentity(step, relatedAssessment, relatedInterviews);
  const matches = events
    .map((event) => ({ event, bindingScore: eventBindingScore(event, identity) }))
    .filter(({ bindingScore }) => bindingScore > 0)
    .sort((left, right) => {
      if (left.bindingScore !== right.bindingScore) return right.bindingScore - left.bindingScore;
      // Multiple events at the same exact source binding are revisions/retries of
      // that source. Prefer its explicit revision; unrelated events never reach
      // this tie because they failed the identity match above.
      const revisionDelta = eventRevision(right.event) - eventRevision(left.event);
      if (revisionDelta) return revisionDelta;
      const timeDelta = eventTimestamp(right.event) - eventTimestamp(left.event);
      if (timeDelta) return timeDelta;
      return right.event.eventId.localeCompare(left.event.eventId);
    });

  return matches[0]?.event || null;
};

export const resolveCandidateEvaluationScore = (candidateEvaluation, ...legacyValues) => {
  const candidates = [
    candidateEvaluation?.current?.fitScore,
    candidateEvaluation?.current?.score,
    ...legacyValues,
  ];
  for (const value of candidates) {
    if (value === undefined || value === null || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return Math.max(0, Math.min(100, Math.round(numeric)));
  }
  return null;
};

/** Resolve the immutable granular report associated with one journey event. */
export const findAssessmentEvaluationForEvent = (relatedAssessment, event) => {
  const eventId = normalizeId(event?.eventId);
  const latestEvaluation = relatedAssessment?.evaluation || null;
  if (!eventId) return latestEvaluation;

  const historicalEvaluation = relatedAssessment?.evaluationHistory?.[eventId];
  if (historicalEvaluation && typeof historicalEvaluation === 'object') {
    return historicalEvaluation;
  }
  if (normalizeId(latestEvaluation?.journeyEventId) === eventId) return latestEvaluation;
  return null;
};

export const getEventScoreRevision = (event) => {
  const revision = event?.scoreRevision || event?.score_revision || {};
  const optionalNumber = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const before = optionalNumber(revision.before ?? revision.scoreBefore ?? event?.scoreBefore);
  const after = optionalNumber(revision.after ?? revision.scoreAfter ?? event?.scoreAfter ?? event?.fitScore);
  const explicitDelta = optionalNumber(revision.delta ?? revision.scoreDelta ?? event?.scoreDelta);
  const hasBefore = before !== null;
  const hasAfter = after !== null;
  const delta = explicitDelta !== null
    ? explicitDelta
    : (hasBefore && hasAfter ? after - before : null);
  const direction = String(revision.direction || event?.scoreDirection || (
    delta !== null ? (delta > 0 ? 'increased' : delta < 0 ? 'decreased' : 'unchanged') : ''
  )).toLowerCase();

  return {
    before: hasBefore ? Math.round(before) : null,
    after: hasAfter ? Math.round(after) : null,
    delta: delta !== null ? Math.round(delta) : null,
    direction,
  };
};

export const limitFactorLabel = (value, maximumWords = 6) => String(value || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, maximumWords)
  .join(' ');
